/**
 * ProjectVaultTab: Main vault component with 3-panel layout
 * Folder tree sidebar | File grid | File details panel
 * Enhanced with folder visibility, color, and sharing support.
 */
import { useState, useCallback, useMemo, useRef } from 'react';
import {
  Search,
  ArrowUpDown,
  FolderPlus,
  ChevronRight,
  FolderOpen,
  FileText,
  Image,
  FileSpreadsheet,
  Archive,
  Video,
  Music,
  FileIcon,
  Ruler,
  HardDrive,
  Files,
  MoreVertical,
  Trash2,
  Pencil,
  Move,
  Copy,
  Download,
  CheckSquare,
  Square,
  XCircle,
  Loader2,
  Shield,
  Lock,
  Users,
  Eye,
} from 'lucide-react';
import { useVault } from '../hooks/useVault';
import { useToast } from './ui/ToastModal';
import { VaultFolderTree } from './vault/VaultFolderTree';
import { VaultUploader, VaultDropZone } from './vault/VaultUploader';
import { VaultFileDetails } from './vault/VaultFileDetails';
import { VaultMoveModal } from './vault/VaultMoveModal';
import { VaultCreateFolderModal } from './vault/VaultCreateFolderModal';
import { VaultShareModal } from './vault/VaultShareModal';
import { VaultFilePreview, isPreviewable } from './vault/VaultFilePreview';
import { VaultConfirmModal } from './vault/VaultConfirmModal';
import { VaultDuplicateModal } from './vault/VaultDuplicateModal';
import type { DuplicateEntry } from './vault/VaultDuplicateModal';
import type { VaultFile, VaultFolder, VaultSortField } from '../types/vault.types';
import { formatFileSize, getFileCategory } from '../types/vault.types';
import { logError } from "../lib/logError";

interface ProjectVaultTabProps {
  projectId: string;
  userId: string;
}

export function ProjectVaultTab({ projectId, userId }: ProjectVaultTabProps) {
  const vault = useVault({ projectId, userId });
  const { addToast } = useToast();

  // Selection state
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Drag-to-move state
  const [dragItemId, setDragItemId] = useState<string | null>(null); // 'file:xxx' or 'folder:xxx'
  const [dropTargetFolderId, setDropTargetFolderId] = useState<string | null>(null);

  // Move/Copy modal
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveMode, setMoveMode] = useState<'move' | 'copy'>('move');
  const [moveFileId, setMoveFileId] = useState<string | null>(null);

  // Create folder modal
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderParentId, setCreateFolderParentId] = useState<string | null | undefined>(undefined);

  // Share/Settings modal
  const [settingsFolder, setSettingsFolder] = useState<VaultFolder | null>(null);

  // File preview
  const [previewFile, setPreviewFile] = useState<VaultFile | null>(null);

  // File context menu, with smart positioning
  const [fileMenu, setFileMenu] = useState<{ fileId: string; x: number; y: number } | null>(null);
  const fileMenuRef = useRef<HTMLDivElement>(null);

  // Confirm modal (replaces browser confirm())
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    variant?: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
  } | null>(null);

  // Duplicate detection modal
  const [duplicateModal, setDuplicateModal] = useState<{
    entries: DuplicateEntry[];
    nonDuplicateFiles: File[];
  } | null>(null);

  // Sort labels
  const sortLabels: Record<VaultSortField, string> = {
    name: 'Name',
    size_bytes: 'Size',
    created_at: 'Upload Date',
    updated_at: 'Modified',
  };

  // Get selected file
  const selectedFile = useMemo(
    () => vault.files.find(f => f.id === selectedFileId) || null,
    [vault.files, selectedFileId],
  );

  // Get current folder name (for create modal context)
  const currentFolderName = useMemo(() => {
    if (createFolderParentId === null || createFolderParentId === undefined) {
      return vault.currentFolderId
        ? vault.allFolders.find(f => f.id === vault.currentFolderId)?.name || null
        : null;
    }
    return vault.allFolders.find(f => f.id === createFolderParentId)?.name || null;
  }, [createFolderParentId, vault.currentFolderId, vault.allFolders]);

  // Load preview URL when file is selected
  const loadPreview = useCallback(async (file: VaultFile) => {
    const category = getFileCategory(file.mime_type, file.name);
    if (category === 'image') {
      try {
        const url = await vault.getDownloadUrl(file.storage_path);
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(null);
      }
    } else {
      setPreviewUrl(null);
    }
  }, [vault]);

  // Clicking the file card (outside the name) → open details panel
  const handleFileClick = useCallback((file: VaultFile) => {
    if (multiSelectMode) {
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        if (next.has(file.id)) {
          next.delete(file.id);
        } else {
          next.add(file.id);
        }
        return next;
      });
    } else {
      setSelectedFileId(file.id);
      loadPreview(file);
    }
  }, [multiSelectMode, loadPreview]);

  // Clicking the file name → open preview (or toast if not previewable)
  const handleFileNameClick = useCallback((e: React.MouseEvent, file: VaultFile) => {
    e.stopPropagation(); // prevent card click (details) from firing
    if (multiSelectMode) return; // in multi-select, let the card handle it
    if (isPreviewable(file)) {
      setPreviewFile(file);
    } else {
      addToast({ type: 'info', title: 'Preview not available', message: `"${file.name}" cannot be previewed. Use Download instead.` });
    }
  }, [multiSelectMode, addToast]);

  const handleDownload = useCallback(async (storagePath: string) => {
    try {
      const url = await vault.getDownloadUrl(storagePath);
      window.open(url, '_blank');
    } catch (err) {
      logError('[Vault] Download failed:', err);
    }
  }, [vault]);

  const handleMove = (fileId: string) => {
    setMoveFileId(fileId);
    setMoveMode('move');
    setMoveModalOpen(true);
  };

  const handleCopy = (fileId: string) => {
    setMoveFileId(fileId);
    setMoveMode('copy');
    setMoveModalOpen(true);
  };

  const handleMoveConfirm = async (targetFolderId: string | null) => {
    if (!moveFileId) return;
    if (moveMode === 'move') {
      await vault.moveFile(moveFileId, targetFolderId);
      if (selectedFileId === moveFileId) setSelectedFileId(null);
    } else {
      await vault.copyFile(moveFileId, targetFolderId);
    }
  };

  const handleBulkDelete = () => {
    if (selectedFileIds.size === 0) return;
    setConfirmModal({
      title: 'Delete Selected Files',
      message: `Are you sure you want to delete ${selectedFileIds.size} selected file(s)? This action cannot be undone.`,
      confirmLabel: 'Delete All',
      variant: 'danger',
      onConfirm: async () => {
        await vault.deleteFiles(Array.from(selectedFileIds));
        setSelectedFileIds(new Set());
        setMultiSelectMode(false);
        setConfirmModal(null);
      },
    });
  };

  const handleBulkDownload = async () => {
    if (selectedFileIds.size === 0) return;
    setBulkDownloading(true);
    try {
      await vault.bulkDownload(Array.from(selectedFileIds));
    } catch (err) {
      logError('[Vault] Bulk download failed:', err);
    } finally {
      setBulkDownloading(false);
    }
  };

  // Upload with duplicate detection
  const handleUploadWithDuplicateCheck = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    // Check for duplicates in the current folder
    const fileNames = files.map(f => f.name);
    const duplicates = await vault.checkDuplicates(fileNames, vault.currentFolderId);

    if (duplicates.length === 0) {
      // No duplicates, upload directly
      await vault.uploadFiles(files);
      return;
    }

    // Build duplicate entries and partition files
    const dupNames = new Set(duplicates.map(d => d.name));
    const nonDuplicateFiles = files.filter(f => !dupNames.has(f.name));
    const duplicateEntries: DuplicateEntry[] = [];

    for (const dup of duplicates) {
      const matchingFile = files.find(f => f.name === dup.name);
      if (matchingFile) {
        duplicateEntries.push({
          existing: dup,
          incoming: matchingFile,
          action: 'skip', // default to skip, user must choose
        });
      }
    }

    // Show the modal
    setDuplicateModal({
      entries: duplicateEntries,
      nonDuplicateFiles,
    });
  }, [vault]);

  // Drag-to-move handlers (supports files AND folders)
  const handleDragStart = (e: React.DragEvent, type: 'file' | 'folder', id: string) => {
    e.dataTransfer.setData('text/plain', `${type}:${id}`);
    e.dataTransfer.effectAllowed = 'move';
    setDragItemId(`${type}:${id}`);
    // For folders, stop the click from also navigating
    e.stopPropagation();
  };

  const handleDragEnd = () => {
    setDragItemId(null);
    setDropTargetFolderId(null);
  };

  const handleFolderDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDragEnter = (e: React.DragEvent, folderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    // Don't highlight if dragging this same folder
    if (dragItemId === `folder:${folderId}`) return;
    setDropTargetFolderId(folderId);
  };

  const handleFolderDragLeave = (e: React.DragEvent) => {
    e.stopPropagation();
    // Only clear drop target if we're leaving the folder card entirely
    // (not just entering a child element like the icon or name)
    const relatedTarget = e.relatedTarget as Node | null;
    if (relatedTarget && e.currentTarget.contains(relatedTarget)) return;
    setDropTargetFolderId(null);
  };

  const handleFolderDrop = async (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = e.dataTransfer.getData('text/plain');
    setDropTargetFolderId(null);
    setDragItemId(null);
    if (!payload) return;

    const [type, id] = payload.split(':');
    if (type === 'file' && id) {
      await vault.moveFile(id, targetFolderId);
    } else if (type === 'folder' && id) {
      // Don't drop folder into itself
      if (id === targetFolderId) return;
      await vault.moveFolder(id, targetFolderId);
    }
  };

  const handleOpenCreateFolder = () => {
    setCreateFolderParentId(undefined); // will use currentFolderId as default
    setCreateFolderOpen(true);
  };

  // Context menu with viewport-aware positioning (computed BEFORE render)
  const MENU_WIDTH = 170;
  const MENU_HEIGHT = 260; // approximate height of menu with all items
  const handleFileContextMenu = (e: React.MouseEvent, fileId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = e.clientX;
    let y = e.clientY;
    // Clamp to viewport
    if (x + MENU_WIDTH > vw - 8) x = vw - MENU_WIDTH - 8;
    if (y + MENU_HEIGHT > vh - 8) y = vh - MENU_HEIGHT - 8;
    if (x < 8) x = 8;
    if (y < 8) y = 8;
    setFileMenu({ fileId, x, y });
  };

  const handlePreview = useCallback((file: VaultFile) => {
    if (isPreviewable(file)) {
      setPreviewFile(file);
    }
  }, []);

  const getFileIcon = (file: VaultFile, size = 28) => {
    const category = getFileCategory(file.mime_type, file.name);
    switch (category) {
      case 'image': return <Image size={size} />;
      case 'pdf': return <FileText size={size} />;
      case 'document': return <FileText size={size} />;
      case 'spreadsheet': return <FileSpreadsheet size={size} />;
      case 'archive': return <Archive size={size} />;
      case 'video': return <Video size={size} />;
      case 'audio': return <Music size={size} />;
      case 'cad': return <Ruler size={size} />;
      default: return <FileIcon size={size} />;
    }
  };

  const getCategoryColor = (file: VaultFile): string => {
    const category = getFileCategory(file.mime_type, file.name);
    const colors: Record<string, string> = {
      image: '#10b981',
      pdf: '#ef4444',
      document: '#3b82f6',
      spreadsheet: '#22c55e',
      archive: '#f59e0b',
      video: '#8b5cf6',
      audio: '#ec4899',
      cad: '#06b6d4',
      other: '#6b7280',
    };
    return colors[category] || colors.other;
  };

  const getVisibilityBadge = (folder: VaultFolder) => {
    if (folder.visibility === 'private') {
      return <Lock size={12} className="vault-folder-badge private" />;
    }
    if (folder.visibility === 'shared') {
      return <Users size={12} className="vault-folder-badge shared" />;
    }
    return null;
  };

  return (
    <div className="vault-container" onClick={() => setFileMenu(null)}>
      {/* ─── Toolbar ───────────────────────────────────────────── */}
      <div className="vault-toolbar">
        <div className="vault-toolbar-left">
          {/* Search */}
          <div className="vault-search">
            <Search size={16} className="vault-search-icon" />
            <input
              type="text"
              placeholder="Search files & folders..."
              value={vault.searchQuery}
              onChange={(e) => vault.setSearchQuery(e.target.value)}
              className="vault-search-input"
            />
            {vault.searchQuery && (
              <button className="vault-search-clear" onClick={() => vault.setSearchQuery('')}>
                <XCircle size={14} />
              </button>
            )}
          </div>

          {/* Sort */}
          <div className="vault-sort">
            <select
              className="vault-sort-select"
              value={vault.sortBy}
              onChange={(e) => vault.setSortBy(e.target.value as VaultSortField)}
            >
              {Object.entries(sortLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <button className="vault-sort-dir" onClick={vault.toggleSortDir} title={vault.sortDir === 'asc' ? 'Ascending' : 'Descending'}>
              <ArrowUpDown size={14} />
              <span className="vault-sort-dir-label">{vault.sortDir === 'asc' ? '↑' : '↓'}</span>
            </button>
          </div>
        </div>

        <div className="vault-toolbar-right">
          {/* Stats */}
          <div className="vault-stats">
            <span className="vault-stat">
              <Files size={14} /> {vault.totalFiles} file{vault.totalFiles !== 1 ? 's' : ''}
            </span>
            <span className="vault-stat">
              <HardDrive size={14} /> {formatFileSize(vault.totalSize)}
            </span>
          </div>

          {/* Multi-select toggle */}
          <button
            className={`btn btn-ghost vault-select-btn ${multiSelectMode ? 'active' : ''}`}
            onClick={() => {
              setMultiSelectMode(!multiSelectMode);
              setSelectedFileIds(new Set());
            }}
          >
            <CheckSquare size={16} />
          </button>

          {multiSelectMode && selectedFileIds.size > 0 && (
            <>
              <button
                className="btn btn-ghost"
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
              >
                {bulkDownloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                <span>{bulkDownloading ? 'Zipping...' : `Download (${selectedFileIds.size})`}</span>
              </button>
              <button className="btn btn-ghost danger" onClick={handleBulkDelete}>
                <Trash2 size={16} />
                <span>Delete ({selectedFileIds.size})</span>
              </button>
            </>
          )}

          {/* New folder */}
          <button className="btn btn-ghost" onClick={handleOpenCreateFolder}>
            <FolderPlus size={16} />
            <span className="hidden sm:inline">New Folder</span>
          </button>

          {/* Upload */}
          <VaultUploader
            uploading={vault.uploading}
            uploadProgress={vault.uploadProgress}
            onUpload={handleUploadWithDuplicateCheck}
            onCancel={vault.cancelUpload}
          />
        </div>
      </div>

      {/* ─── Main Layout ─────────────────────────────────────── */}
      <div className={`vault-layout ${selectedFile ? 'with-details' : ''}`}>
        {/* Sidebar: Folder Tree */}
        <aside className="vault-sidebar">
          <VaultFolderTree
            allFolders={vault.allFolders}
            currentFolderId={vault.currentFolderId}
            currentUserId={userId}
            onNavigate={vault.navigateToFolder}
            onRename={vault.renameFolder}
            onDelete={async (folderId) => {
              const folder = vault.allFolders.find(f => f.id === folderId);
              const folderName = folder?.name || 'this folder';

              // Gather preview data for detailed confirmation
              try {
                const preview = await vault.previewFolderDelete(folderId);
                const parts: string[] = [];
                if (preview.subfolderCount > 0) {
                  parts.push(`${preview.subfolderCount} subfolder${preview.subfolderCount > 1 ? 's' : ''}`);
                }
                if (preview.fileCount > 0) {
                  parts.push(`${preview.fileCount} file${preview.fileCount > 1 ? 's' : ''} (${formatFileSize(preview.totalSizeBytes)})`);
                }

                setConfirmModal({
                  title: 'Delete Folder',
                  message: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <span>
                        Are you sure you want to delete <strong>"{folderName}"</strong>?
                      </span>
                      {parts.length > 0 ? (
                        <div className="vault-delete-breakdown">
                          <span className="vault-delete-breakdown-label">This will permanently delete:</span>
                          <div className="vault-delete-breakdown-chips">
                            {preview.subfolderCount > 0 && (
                              <span className="vault-delete-chip folders">
                                📁 {preview.subfolderCount} subfolder{preview.subfolderCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {preview.fileCount > 0 && (
                              <span className="vault-delete-chip files">
                                📄 {preview.fileCount} file{preview.fileCount > 1 ? 's' : ''} ({formatFileSize(preview.totalSizeBytes)})
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          This folder is empty.
                        </span>
                      )}
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 4 }}>
                        This action cannot be undone.
                      </span>
                    </div>
                  ),
                  confirmLabel: 'Delete Folder',
                  variant: 'danger',
                  onConfirm: async () => {
                    try {
                      await vault.deleteFolder(folderId);
                      addToast({ type: 'success', title: `Folder "${folderName}" deleted successfully` });
                    } catch {
                      addToast({ type: 'error', title: 'Failed to delete folder' });
                    }
                    setConfirmModal(null);
                  },
                });
              } catch {
                addToast({ type: 'error', title: 'Failed to preview folder contents' });
              }
            }}
            onCreateFolder={handleOpenCreateFolder}
            onCreateSubfolder={(parentFolder) => {
              setCreateFolderParentId(parentFolder.id);
              setCreateFolderOpen(true);
            }}
            onOpenSettings={(folder) => setSettingsFolder(folder)}
            onUpdateColor={vault.updateFolderColor}
            onMoveFile={vault.moveFile}
            onMoveFolder={vault.moveFolder}
          />
        </aside>

        {/* Main Content: File Grid */}
        <VaultDropZone onDrop={handleUploadWithDuplicateCheck} uploading={vault.uploading}>
          <main className="vault-main">
            {/* Breadcrumbs */}
            <nav className="vault-breadcrumbs">
              {vault.breadcrumbs.map((crumb, i) => (
                <span key={crumb.id ?? 'root'} className="vault-breadcrumb-item">
                  {i > 0 && <ChevronRight size={14} className="vault-breadcrumb-sep" />}
                  <button
                    className={`vault-breadcrumb-btn ${i === vault.breadcrumbs.length - 1 ? 'current' : ''}`}
                    onClick={() => vault.navigateToFolder(crumb.id)}
                  >
                    {crumb.name}
                  </button>
                </span>
              ))}
            </nav>

            {/* Loading */}
            {vault.loading && (
              <div className="vault-loading">
                <Loader2 size={32} className="spin" />
                <p>Loading vault...</p>
              </div>
            )}

            {/* Content */}
            {!vault.loading && (
              <>
                {/* Search indicator */}
                {vault.isSearchActive && (
                  <div className="vault-search-indicator">
                    <Search size={14} />
                    <span>Showing results across all folders for “{vault.searchQuery}”</span>
                  </div>
                )}

                {/* Folders */}
                {vault.folders.length > 0 && (
                  <div className="vault-section">
                    <h4 className="vault-section-title">Folders</h4>
                    <div className="vault-grid folders">
                      {vault.folders.map(folder => (
                        <div
                          key={folder.id}
                          className={`vault-folder-card ${dropTargetFolderId === folder.id ? 'drop-target' : ''} ${dragItemId === `folder:${folder.id}` ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'folder', folder.id)}
                          onDragEnd={handleDragEnd}
                          onDoubleClick={() => vault.navigateToFolder(folder.id)}
                          onClick={() => vault.navigateToFolder(folder.id)}
                          onDragOver={handleFolderDragOver}
                          onDragEnter={(e) => handleFolderDragEnter(e, folder.id)}
                          onDragLeave={(e) => handleFolderDragLeave(e)}
                          onDrop={(e) => handleFolderDrop(e, folder.id)}
                        >
                          <FolderOpen
                            size={28}
                            className="vault-folder-card-icon"
                            style={{ color: folder.color || '#3b82f6' }}
                          />
                          <span className="vault-folder-card-name">{folder.name}</span>
                          {getVisibilityBadge(folder)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Files */}
                {vault.files.length > 0 && (
                  <div className="vault-section">
                    <h4 className="vault-section-title">Files</h4>
                    <div className="vault-grid files">
                      {vault.files.map(file => (
                        <div
                          key={file.id}
                          className={`vault-file-card ${selectedFileId === file.id ? 'selected' : ''} ${selectedFileIds.has(file.id) ? 'multi-selected' : ''} ${dragItemId === `file:${file.id}` ? 'dragging' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'file', file.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => handleFileClick(file)}
                          onContextMenu={(e) => handleFileContextMenu(e, file.id)}
                        >
                          {multiSelectMode && (
                            <div className="vault-file-checkbox">
                              {selectedFileIds.has(file.id)
                                ? <CheckSquare size={16} className="checked" />
                                : <Square size={16} />}
                            </div>
                          )}
                          <div className="vault-file-card-icon" style={{ color: getCategoryColor(file) }}>
                            {getFileIcon(file)}
                          </div>
                          <div className="vault-file-card-info">
                            <span
                              className="vault-file-card-name clickable"
                              title={`Click to preview ${file.name}`}
                              onClick={(e) => handleFileNameClick(e, file)}
                            >{file.name}</span>
                            <span className="vault-file-card-meta">
                              {formatFileSize(file.size_bytes)} · {new Date(file.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          <button
                            className="vault-file-card-menu"
                            onClick={(e) => { e.stopPropagation(); handleFileContextMenu(e, file.id); }}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {vault.folders.length === 0 && vault.files.length === 0 && (
                  <div className="vault-empty">
                    <div className="vault-empty-icon">
                      <Shield size={52} />
                    </div>
                    <h3>Your Vault is Empty</h3>
                    <p>Drag & drop files here or click Upload to get started.</p>
                    <p className="vault-empty-hint">Create folders to organize your project documents.</p>
                  </div>
                )}
              </>
            )}
          </main>
        </VaultDropZone>

        {/* Details panel */}
        {selectedFile && !multiSelectMode && (
          <VaultFileDetails
            key={selectedFile.id}
            file={selectedFile}
            previewUrl={previewUrl}
            onClose={() => { setSelectedFileId(null); setPreviewUrl(null); }}
            onDownload={handleDownload}
            onRename={async (id, name) => {
              await vault.renameFile(id, name);
            }}
            onDelete={(id) => {
              const file = vault.files.find(f => f.id === id);
              setConfirmModal({
                title: 'Delete File',
                message: `Are you sure you want to delete "${file?.name || 'this file'}"? This action cannot be undone.`,
                confirmLabel: 'Delete',
                variant: 'danger',
                onConfirm: async () => {
                  await vault.deleteFile(id);
                  setSelectedFileId(null);
                  setPreviewUrl(null);
                  setConfirmModal(null);
                },
              });
            }}
            onMove={handleMove}
            onCopy={handleCopy}
          />
        )}
      </div>

      {/* File context menu */}
      {fileMenu && (() => {
        const file = vault.files.find(f => f.id === fileMenu.fileId);
        if (!file) return null;
        const canPreview = isPreviewable(file);
        return (
          <div
            ref={fileMenuRef}
            className="vault-context-menu"
            style={{ position: 'fixed', top: fileMenu.y, left: fileMenu.x, zIndex: 9999 }}
            onClick={(e) => e.stopPropagation()}
          >
            {canPreview && (
              <button onClick={() => { handlePreview(file); setFileMenu(null); }}>
                <Eye size={14} /> Preview
              </button>
            )}
            <button onClick={() => { handleDownload(file.storage_path); setFileMenu(null); }}>
              <Download size={14} /> Download
            </button>
            <div className="vault-context-divider" />
            <button onClick={() => { handleMove(file.id); setFileMenu(null); }}>
              <Move size={14} /> Move
            </button>
            <button onClick={() => { handleCopy(file.id); setFileMenu(null); }}>
              <Copy size={14} /> Copy
            </button>
            <button onClick={() => {
              setSelectedFileId(file.id);
              loadPreview(file);
              setFileMenu(null);
            }}>
              <Pencil size={14} /> Details
            </button>
            <div className="vault-context-divider" />
            <button className="danger" onClick={() => {
              setFileMenu(null);
              setConfirmModal({
                title: 'Delete File',
                message: `Are you sure you want to delete "${file.name}"? This action cannot be undone.`,
                confirmLabel: 'Delete',
                variant: 'danger',
                onConfirm: async () => {
                  await vault.deleteFile(file.id);
                  if (selectedFileId === file.id) setSelectedFileId(null);
                  setConfirmModal(null);
                },
              });
            }}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        );
      })()}

      {/* Move/Copy modal */}
      <VaultMoveModal
        isOpen={moveModalOpen}
        mode={moveMode}
        allFolders={vault.allFolders}
        currentFolderId={vault.currentFolderId}
        onConfirm={handleMoveConfirm}
        onClose={() => { setMoveModalOpen(false); setMoveFileId(null); }}
      />

      {/* Create Folder modal */}
      <VaultCreateFolderModal
        isOpen={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreate={vault.createFolder}
        parentFolderName={currentFolderName}
        parentFolderId={createFolderParentId ?? vault.currentFolderId ?? null}
        vaultUsers={vault.vaultUsers}
      />

      {/* Settings/Share modal */}
      {settingsFolder && (
        <VaultShareModal
          isOpen={!!settingsFolder}
          onClose={() => setSettingsFolder(null)}
          folder={settingsFolder}
          vaultUsers={vault.vaultUsers}
          currentUserId={userId}
          onUpdateVisibility={vault.updateFolderVisibility}
          onUpdateColor={vault.updateFolderColor}
          onGetShares={vault.getFolderShares}
        />
      )}

      {/* File Preview Lightbox */}
      {/* Confirm Modal (replaces browser confirm) */}
      <VaultConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel}
        variant={confirmModal?.variant}
        onConfirm={() => confirmModal?.onConfirm()}
        onCancel={() => setConfirmModal(null)}
      />

      {/* Duplicate File Modal */}
      {duplicateModal && (
        <VaultDuplicateModal
          isOpen={!!duplicateModal}
          entries={duplicateModal.entries}
          nonDuplicateCount={duplicateModal.nonDuplicateFiles.length}
          onCancel={() => setDuplicateModal(null)}
          onConfirm={async (resolvedEntries) => {
            setDuplicateModal(null);
            const filesToUpload: File[] = [...duplicateModal.nonDuplicateFiles];

            for (const entry of resolvedEntries) {
              if (entry.action === 'skip') continue;
              if (entry.action === 'replace') {
                // Delete old, upload new
                try {
                  await vault.replaceFile(
                    entry.existing.id,
                    entry.incoming as File,
                    vault.currentFolderId,
                  );
                } catch (err) {
                  logError('[Vault] Replace failed:', err);
                }
              } else if (entry.action === 'keep-both') {
                // Auto-rename: "file.pdf" → "file (1).pdf"
                const f = entry.incoming as File;
                const dotIdx = f.name.lastIndexOf('.');
                const base = dotIdx > 0 ? f.name.slice(0, dotIdx) : f.name;
                const ext = dotIdx > 0 ? f.name.slice(dotIdx) : '';
                const newName = `${base} (copy)${ext}`;
                const renamedFile = new File([f], newName, { type: f.type });
                filesToUpload.push(renamedFile);
              }
            }

            // Upload any remaining non-duplicate + keep-both files
            if (filesToUpload.length > 0) {
              await vault.uploadFiles(filesToUpload);
            }
            vault.refresh();
          }}
        />
      )}

      {previewFile && (
        <VaultFilePreview
          file={previewFile}
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
          onDownload={handleDownload}
          getDownloadUrl={vault.getDownloadUrl}
        />
      )}
    </div>
  );
}
